/**
 * Tenant feature — Zod schemas and derived TypeScript types.
 *
 * One file per feature. These are the single source of truth for the
 * shape of tenant-related data at every network boundary:
 *
 *   - Request/response validation in query hooks.
 *   - URL search param validation in route files.
 *   - Form input validation via zodResolver.
 *
 * No other file in the feature may define a tenant type — import
 * from here, or extend the schema here and import the new type.
 */

import { z } from "zod";


// ── List & filtering ──────────────────────────────────────────────────────

// The four-value tenant lifecycle vocabulary. Authority is the Go
// `isValidStatus` switch (backend/internal/tenant/tenant/tenant.go) and
// the `tenants_status_check` CHECK constraint in
// db/migrations/000001_init.up.sql — this enum must mirror both exactly.
// `deleted` is a terminal tombstone state, not a synonym for absent:
// the row survives and the list endpoint returns it, so parsing must
// admit it. There is no `archived` status anywhere in the backend.
export const tenantStatusSchema = z.enum([
  "active",
  "suspended",
  "deactivated",
  "deleted",
]);
export type TenantStatus = z.infer<typeof tenantStatusSchema>;

// Archetype filter values. Mirrors the ArchetypeId enum in
// features/tenants/archetype.ts — kept here as a plain zod enum to
// avoid a cycle (schemas.ts must stay side-effect-free and
// dependency-free so every feature can import it).
export const archetypeFilterSchema = z.enum([
  "infrastructure",
  "workforce_full_idp",
  "workforce_passkey_layer",
  "customer_full_idp",
  "customer_enterprise_fed",
]);
export type ArchetypeFilter = z.infer<typeof archetypeFilterSchema>;

export const tenantTypeFilterSchema = z.enum(["infrastructure", "workforce", "customer"]);
export type TenantTypeFilter = z.infer<typeof tenantTypeFilterSchema>;

export const customerAudienceSchema = z.enum([
  "unspecified", "organization_members", "individual_customers", "mixed",
]);
export type CustomerAudience = z.infer<typeof customerAudienceSchema>;

export const tenantsListSearchSchema = z.object({
  type: tenantTypeFilterSchema.optional(),
  customer_audience: customerAudienceSchema.optional(),
  q: z.string().optional(),
  status: tenantStatusSchema.optional(),
  archetype: archetypeFilterSchema.optional(),
  sortBy: z.enum(["name", "slug", "created_at"]).optional(),
  sortOrder: z.enum(["asc", "desc"]).optional(),
  offset: z.coerce.number().int().nonnegative().optional(),
}).transform((search) => search.customer_audience
  ? { ...search, type: "customer" as const, archetype: undefined }
  : search);
export type TenantsListSearch = z.infer<typeof tenantsListSearchSchema>;

// ── Classification (immutable once a tenant is created) ──────────────────

// These five dimensions compose into the tenant "archetype". Every
// value is enforced by a DB CHECK constraint (see db/migrations/000001_init.up.sql (was 02_tenants.sql))
// and none is mutable via the UpdateTenantRequest — they define the
// tenant's architecture at creation and never change. See
// features/tenants/archetype.ts for the descriptor that names each
// legal combination.

export const tenantTypeSchema = z.enum(["workforce", "customer"]);
export type TenantType = z.infer<typeof tenantTypeSchema>;

export const userSourceSchema = z.enum([
  "complete_platform",
  "existing_idp",
  "enterprise_fed",
]);
export type UserSource = z.infer<typeof userSourceSchema>;

export const platformRoleSchema = z.enum(["full_idp"]);
export type PlatformRole = z.infer<typeof platformRoleSchema>;

export const identitySourceSchema = z.enum(["builtin", "external"]);
export type IdentitySource = z.infer<typeof identitySourceSchema>;

export const externalProviderSchema = z.enum([
  "entra",
  "okta",
  "ping",
  "google",
  "oidc-saml",
]);
export type ExternalProvider = z.infer<typeof externalProviderSchema>;

// ── Tenant record (list view) ─────────────────────────────────────────────

// Every field is required. The backend always populates them for
// every tenant row; any missing field is a backend bug that should
// crash the query loudly rather than render a silent zero / unknown.
// external_provider is the only honest optional — it's absent for
// tenants with identity_source=builtin.
export const tenantShapeSchema = z.enum([
  "workforce", "customer_org_members", "customer_individuals", "customer_mixed", "infrastructure",
]);

export { tenantListItemSchema, tenantsListResponseSchema } from "@/lib/tenant-inventory-contract";
export type TenantListItem = z.infer<typeof import("@/lib/tenant-inventory-contract").tenantListItemSchema>;
export type TenantsListResponse = z.infer<typeof import("@/lib/tenant-inventory-contract").tenantsListResponseSchema>;


// ── Tenant detail / settings ──────────────────────────────────────────────

/**
 * Settings are intentionally modeled as a loose record at the top
 * level — the Go backend stores them as a versioned JSON blob and
 * new fields land independently of frontend releases. Each specific
 * section (auth methods, SAML config, etc.) defines its own
 * sub-schema in its own feature folder and parses the relevant slice
 * here with `.pick()`.
 */
const webauthnConfigSchema = z.object({
  user_verification: z.string(),
  attestation: z.string(),
  authenticator_attachment: z.string(),
  resident_key: z.string(),
  backup_eligibility: z.string(),
});

// Every scalar the backend emits for a tenant is required here.
// Nullable fields (support_email, logo_url, etc.) are nullable on
// the wire because the database column is nullable; they use
// `.nullable()` rather than `.optional()` — absence IS the null,
// not a missing field. external_provider is absent for builtin identity;
// per-kind caps are optional in TenantTenantResponse for older servers.
export const tenantDetailSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  status: tenantStatusSchema,
  is_infrastructure: z.boolean(),
  tenant_type: tenantTypeSchema,
  customer_audience: customerAudienceSchema,
  user_source: userSourceSchema,
  platform_role: platformRoleSchema,
  identity_source: identitySourceSchema,
  external_provider: externalProviderSchema.optional(),
  /** The console shape the server derived; absent only from a server that predates it. */
  shape: tenantShapeSchema,
  organizations_enabled: z.boolean(),
  require_unique_employee_number: z.boolean(),
  /** A departed person keeps their employee number until their record is deleted. */
  hold_employee_number_after_departure: z.boolean().default(false),
  require_unique_primary_email: z.boolean(),
  rp_id: z.string(),
  rp_origins: z.array(z.string()),
  /** Canonical protocol host for this tenant — {slug}.{base_domain}
   *  (e.g. acme.us.pureid.ai). All SAML, OIDC, and OAuth endpoints are
   *  served here; the OIDC issuer is https://{auth_host}. Always use this
   *  (not rp_id) when constructing any SAML/OIDC/OAuth URL shown to an
   *  operator. Derived server-side so the UI never reconstructs it. */
  auth_host: z.string(),
  webauthn: webauthnConfigSchema,
  created_at: z.string(),
  updated_at: z.string(),
  user_count: z.number().int().nonnegative(),
  passkey_count: z.number().int().nonnegative(),
  application_count: z.number().int().nonnegative(),
  login_passkey_button: z.boolean(),
  login_passkey_button_label: z.string(),
  login_default_hint: z.string().nullable(),
  enrollment_transports: z.array(z.string()),
  auth_methods: z.array(z.string()),
  passkey_required: z.boolean(),
  max_passkeys_per_user: z.number().int().nonnegative(),
  // Optional in TenantTenantResponse. Absent is unknown; explicit 0 = unlimited.
  max_synced_passkeys_per_user: z.number().int().nonnegative().max(50).optional(),
  max_device_bound_passkeys_per_user: z.number().int().nonnegative().max(50).optional(),
  max_security_keys_per_user: z.number().int().nonnegative().max(50).optional(),
  max_workstation_logon_credentials_per_user: z.number().int().nonnegative().max(50).optional(),
  session_lifetime_minutes: z.number().int().positive(),
  admin_idle_timeout_minutes: z.number().int().positive(),
  /** The deployment's limits the two values above are held to; the
   *  tenant's own values only shorten them. Absent when unknown. */
  session_lifetime_max_minutes: z.number().int().positive().optional(),
  admin_idle_timeout_max_minutes: z.number().int().positive().optional(),
  audit_retention_days: z.number().int().min(30).max(2555),
  /** Server-computed canonical SCIM endpoint URL. Always present
   *  — the backend derives it from the tenant's rp_id at DTO
   *  mapping time regardless of whether the tenant accepts SCIM
   *  traffic. UI gates show/hide by archetype, not presence. */
  scim_endpoint_url: z.string(),
  support_email: z.string().nullable(),
  logo_url: z.string().nullable(),
  favicon_url: z.string().nullable(),
  primary_color: z.string().nullable(),
  custom_email_from: z.string().nullable(),
  custom_domain: z.string().nullable(),
  /** GDAP-style consent flag — TRUE when the tenant has authorised
   *  platform admins to issue admin_tenant_grants into this tenant
   *  (the MSSP / managed-IGA model). Tenant admins flip this via
   *  Settings → Delegation; toggling does NOT affect existing
   *  admin_tenant_grants rows. See
   *  docs/product-areas.md. */
  platform_managed_delegation: z.boolean(),
  // ── C.1 Phase 1 — badge / smart card / PIV federation surface ─────
  /** When TRUE, the portal /login renders a prominent "Tap your
   *  badge to sign in" affordance that delegates to the chosen
   *  federation provider. Default FALSE. */
  badge_signin_enabled: z.boolean().nullable().optional(),
  /** Federation provider UUID that owns the badge IdP. Null when
   *  badge sign-in is disabled OR the provider was deleted (FK
   *  ON DELETE SET NULL auto-clears). */
  badge_signin_provider_id: z.string().nullable().optional(),
  /** Optional override for the user-visible button label. */
  badge_signin_label: z.string().nullable().optional(),
  /** Auto-redirect on page load for shared workstations. */
  badge_signin_kiosk_mode: z.boolean().nullable().optional(),
  /** Admits the first-party PureID mobile authenticator app. Deny-by-default;
   *  Android-effective only (iOS asserts the canonical https origin). */
  mobile_authenticator_enabled: z.boolean().nullable().optional(),
  /** Accepts remote-desktop relayed (cross-origin) passkey assertions for
   *  sign-in. Deny-by-default; authentication only. */
  allow_remote_desktop_relay: z.boolean().nullable().optional(),
  // ── Frontline routing + Kiosk Pass policy ────────────────────────
  frontline_enabled: z.boolean().optional(),
  frontline_kiosk_pass_enabled: z.boolean().optional(),
  /** Per-method Kiosk Pass policy. Empty array = "T0 default:
   *  every registered method enabled when the master is ON".
   *  Non-empty array = explicit per-method curation. */
  frontline_kiosk_pass_methods: z
    .array(
      z.object({
        id: z.string(),
        enabled: z.boolean(),
        config: z.record(z.string(), z.unknown()).default({}),
      }),
    )
    .default([]),
  frontline_pin_method_enabled: z.boolean().optional(),
  frontline_nfc_method_enabled: z.boolean().optional(),
});
export type TenantDetail = z.infer<typeof tenantDetailSchema>;

// ── Create / update ───────────────────────────────────────────────────────

// Backend contract: backend/pkg/api/tenant/requests.go:CreateTenantRequest
// and the cross-field rules in tenant.go:validateCrossFields. DecodeJSON
// uses DisallowUnknownFields — every key sent here must be on the Go
// struct, or the request 400s. Enum schemas live in the classification
// section above so list, detail, and create paths share one set of
// legal values.

export const createTenantSchema = z
  .object({
    name: z.string().min(1, "Name is required").max(80, "Name too long"),
    slug: z
      .string()
      .min(1, "Slug is required")
      .max(40, "Slug too long")
      .regex(
        /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/,
        "Use lowercase letters, digits, and dashes",
      ),
    tenant_type: tenantTypeSchema,
    user_source: userSourceSchema,
    external_provider: externalProviderSchema.optional(),
    organizations_enabled: z.boolean().optional(),
    deployment_goal: z.enum(["passwordless", "application_sign_in"]),
    // Audience is the one customer dimension that is shape, so
    // the console asks for it on every customer tenant. The API still accepts
    // its absence, which resolves to the mixed shape until set.
    customer_audience: customerAudienceSchema.optional(),
  })
  .superRefine((data, ctx) => {
    if (data.tenant_type === "customer" && (!data.customer_audience || data.customer_audience === "unspecified")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["customer_audience"],
        message: "Choose who the customers are. It decides which parts of the console this tenant gets.",
      });
    }
    // tenant_type × user_source: mirrors chk_user_source_type in
    // db/migrations/000001_init.up.sql (was 02_tenants.sql). Catch the mismatch in the form so the
    // operator sees the error next to the picker, not in a toast.
    const allowed: Record<TenantType, UserSource[]> = {
      workforce: ["complete_platform", "existing_idp"],
      customer: ["complete_platform", "enterprise_fed"],
    };
    if (!allowed[data.tenant_type].includes(data.user_source)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["user_source"],
        message:
          data.tenant_type === "workforce"
            ? "Workforce tenants support the built-in directory or existing-IdP sync."
            : "Customer tenants support the built-in directory or enterprise federation.",
      });
    }
    // external_provider required when the user source is external
    // (existing_idp / enterprise_fed), forbidden for complete_platform.
    const externalSource =
      data.user_source === "existing_idp" ||
      data.user_source === "enterprise_fed";
    if (externalSource && !data.external_provider) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["external_provider"],
        message: "Select an identity provider.",
      });
    }
    if (!externalSource && data.external_provider) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["external_provider"],
        message:
          "Built-in directory doesn't use an external provider. Clear this field.",
      });
    }
  });
export type CreateTenantInput = z.infer<typeof createTenantSchema>;

// Server response for GET /api/v1/tenants/check-slug?slug=…
export const slugAvailabilitySchema = z.object({
  available: z.boolean(),
  reason: z
    .enum(["taken", "reserved", "invalid_format"])
    .optional()
    .or(z.literal("")),
});
export type SlugAvailability = z.infer<typeof slugAvailabilitySchema>;

// UpdateTenantInput is a permissive bag for the many backend
// UpdateTenantRequest fields (branding, WebAuthn config, login
// chrome, etc.). We do NOT derive this from createTenantSchema —
// the two surfaces are deliberately different: create accepts
// only identity fields (name, slug), update accepts every
// post-create knob. Each caller (useSaveRpSettings, branding
// mutation, etc.) sends its own narrow slice; this base type
// just documents the shared shape.
export type UpdateTenantInput = Record<string, unknown>;

// ── Overview rollup ───────────────────────────────────────────────────────
//
// Backs the /tenants/{id}/overview endpoint (platform-admin drill-in)
// and /api/tenant/v1/overview (tenant-admin on own tenant). Mirrors
// PlatformOverview structure so the dashboard page components are
// identical to the platform dashboard's.

// The wire shapes are TenantOverview and its parts in api/openapi/operations.yaml, generated into
// overview.generated.ts.
export { tenantKpisSchema, tenantOverviewSchema } from "./overview.generated";
import type { tenantKpisSchema, tenantOverviewSchema } from "./overview.generated";
export type TenantKpis = z.infer<typeof tenantKpisSchema>;
export type TenantOverview = z.infer<typeof tenantOverviewSchema>;
